<template>
  <div class="container py-6">

    <div 
      class="columns is-multiline productionsheet__info" 
      v-if="!loading">
      <div class="column is-3">
        <label class="label">Project Name</label>
        <span>
          {{ productionSheet.project.project_name }}
        </span>
      </div>
      <div class="column is-3">
        <label class="label">Production Sheet Name</label>
        <v-text-field 
          class="noprint"
          :disabled="!editable"
          v-model="productionSheet.name"
          @blur="update('name', $event)">
        </v-text-field>
        <span class="printonly">
          {{ productionSheet.desc }}
        </span>
      </div>
      <div class="column is-3">
        <label class="label">Description</label>
        <v-text-field 
          class="noprint"
          :disabled="!editable"
          v-model="productionSheet.desc"
          @blur="update('desc', $event)">
        </v-text-field>
        <span class="printonly">
          {{ productionSheet.desc }}
        </span>
      </div>
      <div class="column is-3">
        <label class="label">Created On</label>
        <span>
          {{ format(parse(productionSheet.created_on)) }}
        </span>
      </div>
    </div>

    <hr class="noprint">

    <div class="buttons noprint" v-if="editable">
      <button
        @click="addingItem = true" 
        class="button is-primary">
        Add an item
      </button>

      <v-fade-transition mode="out-in">
        <button class="button"
          v-if="selected.length > 0" 
          @click="launchConfirmDelete()">
          <span class="icon is-medium">
            <i class="fas fa-trash has-text-danger"></i>
          </span>
          <span>Delete {{ selected.length }} Selected Items</span>
        </button>
      </v-fade-transition>
    </div>

    <article 
      v-if="!loading && productionSheet.items.length == 0" 
      class="message is-info is-fullwidth">
      <div class="message-body">
        There are no items in this production sheet.
      </div>
    </article>

    <table 
      v-if="!loading && productionSheet.items.length > 0" 
      class="table is-fullwidth is-hoverable has-smallish-font">

      <thead>
        <tr class="is-capitalized">
          <th class="noprint">
            <v-checkbox
              v-if="editable"
              :value="allSelected"
              @input="toggleAllSelected($event)">
            </v-checkbox>
          </th>
          <th 
            v-for="(header, headerIndex) in headers" 
            v-bind="header.bind"
            :key="headerIndex">
            {{ header.label }}
          </th>
          <th class="noprint" v-if="editable"></th>
        </tr>
      </thead>

      <tbody v-if="!loading">
        <tr v-for="item in productionSheet.items" :key="item.id">

          <th class="noprint">
            <v-checkbox
              class="mt-2"
              v-if="editable"
              :value="selected.includes(item.id)"
              @input="toggleSelected(item.id)">
            </v-checkbox>
          </th>
          <td>
            {{ item.backing_product_master.name }}
          </td>
          <td>
            {{ item.b_thick_in_str }}
          </td>
          <td>
            {{ item.facing_product_master.name }}
          </td>
          <td>
            {{ item.b_thick_in_str }}
          </td>
          <td>
            <span v-if="item.finishing_step">
              {{ item.finishing_step.processing_name }}
            </span>
            <span v-else class="has-text-grey">
              No finishing
            </span>
          </td>
          <td class="noprint">
            {{ item.slab_product_master.name }}
          </td>
          <td>
            {{ lwt(item) }}
          </td>

          <td>
            <v-text-field
              required
              v-if="item.id == editingId"
              placeholder="Finished Quantity"
              v-model="item.finished_qty"/>
            <span v-else>
              {{ item.finished_qty }}
            </span>
          </td>

          <td>
            {{ item.qty_to_be_ordered }}
          </td>

          <td>
            <v-text-field
              required
              v-if="item.id == editingId"
              placeholder="Item Location"
              v-model="item.location"/>
            <span v-else>
              {{ item.location }}
            </span>
          </td>

          <td>
            <v-text-field
              required
              v-if="item.id == editingId"
              placeholder="Item Description"
              v-model="item.desc"/>
            <span v-else>
              {{ item.desc }}
            </span>
          </td>

          <td class="noprint" v-if="editable">
            <div class="buttons buttons-right mt-1">
              <template v-if="item.id != editingId">
                <button
                  @click="editingId = item.id"
                  class="button is-small">
                  <span class="has-text-grey icon">
                    <i class="fas fa-edit"></i>
                  </span>
                </button>
                <button  
                  @click="launchConfirmDelete(item.id)"
                  class="button is-small">
                  <span class="has-text-grey icon">
                    <i class="fas fa-trash"></i>
                  </span>
                </button>
              </template>
              <template v-else>
                <button  
                  @click="saveEdits(item)"
                  class="button is-small is-primary">
                  <span class="icon">
                    <i class="fas fa-save"></i>
                  </span>
                </button>
                <button  
                  @click="editingId = null"
                  class="button is-small">
                  <span class="icon has-text-grey">
                    <i class="fas fa-times"></i>
                  </span>
                </button>
              </template>
            </div>
          </td>

        </tr>
      </tbody>
    </table>

    <hr class="noprint">

    <div class="columns">

      <div 
        class="column is-half">
        <label class="label">Drawing</label>
        <div class="is-relative">
          <v-overlay
            class="noprint" 
            absolute 
            show-on-hover>
            <div class="is-fullheight flex flex--row flex--align-center flex--justify-center">
              <v-file-upload 
                :disabled="!editable"
                :href="productionSheet.image"
                file-type-validator=".*"
                :value="{
                  file: null,
                  name: 'Change Upload File',
                  url: productionSheet.image
                }"
                @input="changedImage($event)">
                attachment
              </v-file-upload>
            </div>
          </v-overlay>
          <component 
            v-if="productionSheet && productionSheet.image" 
            :is="attachmentType" 
            :src="productionSheet.image"
            frameBorder="0"/>
          <div class="placeholder" v-else>
          </div>
        </div>
      </div>

      <div
        class="column is-half">
        <label class="label">Material Samples</label>
        <div class="columns is-multiline">
          <div 
            class="column is-one-third has-text-centered" 
            v-for="product in products" 
            :key="product.id">
            <img :src="product.image" :alt="product.name"/>
            <span class="is-size-7">{{ product.name }}</span>
          </div>
        </div>
      </div>

    </div>

    <v-confirm-modal 
      v-model="confirmDelete.state"
      :title="confirmDelete.title"
      :message="confirmDelete.message"
      :type="confirmDelete.type"
      @confirm="deleteConfirmedCb()"
      @cancel="confirmDelete.state = false"/>

    <add-production-sheet-item-modal
      v-if="!loading"
      :production-sheet-id="productionSheet.id"
      v-model="addingItem"/>

    <v-file-preview 
      v-if="!loading && productionSheet.image"
      v-model="previewing" 
      :src="productionSheet.image"
      :tag="productionSheet.image.includes('pdf') ? 
        'iframe' : 'img'"/>

  </div>
</template>

<script>
import VConfirmModal from '@/components/base/VConfirmModal'
import VCheckbox from '@/components/base/VCheckbox'
import VTextField from '@/components/base/VTextField'
import VFileUpload from '@/components/base/VFileUpload'
import VFilePreview from '@/components/base/VFilePreview'
import VOverlay from '@/components/base/VOverlay'
import VFadeTransition from '@/components/transitions/VFadeTransition'

import AddProductionSheetItemModal from '@/components/inventory/AddProductionSheetItemModal'

import { mapState, mapActions, mapMutations } from 'vuex'

import { parseISO, format } from 'date-fns'

export default {
  components: { 
    VConfirmModal, VCheckbox, VTextField, VFileUpload, 
    VFilePreview, VFadeTransition, VOverlay,
    AddProductionSheetItemModal
  },
  data() {
    return {
      headers: [
        { label: 'Backing Product' },
        { label: 'Backing Thickness' },
        { label: 'Facing Product' },
        { label: 'Facing Thickness' },
        { label: 'Facing Finish' },
        { label: 'Finished Slab', bind: { class: 'noprint' }},
        { label: 'Finished Slab Size' },
        { label: 'Finished Quantity' },
        { label: 'Order Quantity' },
        { label: 'Location' },
        { label: 'Description' }
      ],
      editingId: null,
      confirmDelete: {
        state: false,
        title: 'Delete Confirmation',
        message: 'Are you sure you want to delete this BOM Detail?',
        type: 'danger'
      },
      deleteConfirmedCb: null,
      addingItem: false,
      previewing: false,
      parse: (d) => {
        return d ? parseISO(d) : null
      },
      format: (d) => {
        return d ? format(d, 'yyyy-MM-dd H:mm:ss') : null
      } 
    }
  },
  computed: {
    ...mapState("products", ["products"]),
    ...mapState("productionSheet", [
      "productionSheet", "selected", "allSelected", "loading"
    ]),
    editable() {
      return true;
    },
    productionSheetId() {
      return this.$route.params.productionSheetId;
    },
    attachmentType({ productionSheet }) {
      if (productionSheet.image) {
        if (productionSheet.image.includes('.pdf')) {
          return 'iframe'
        } else {
          return 'img'
        }
      } else {
        return null
      }
    },
    products({ productionSheet }) {
      let res = [];
      productionSheet.items.forEach(item => {
        if (!res.map(d => d.id).includes(item.facing_product_master.id)) {
          res.push(item.facing_product_master);
        }
      });
      return res;
    }
  },
  created() {
    this.initialize();
  },
  methods: {
    ...mapActions("products", ["getProducts"]),
    ...mapActions("productionSheet", [
      "getProductionSheet", "updateProductionSheet", "uploadImage",
      "addItem", "updateItem", "deleteItem", "toggleAllSelected"
    ]),
    ...mapMutations("productionSheet", [
      "setProductionSheet", "toggleSelected"
    ]),
    ...mapMutations("app", ["setNotification"]),
    initialize() {
      if (this.$route.params.productionSheet) {
        this.setProductionSheet(this.$route.params.productionSheet);
      } else {
        this.getProductionSheet(this.productionSheetId);
      }

      if (this.products.length == 0) {
        this.getProducts();
      }
    },
    lwt(v) {
      return `L${v.length_in_str}" x W${v.width_in_str}" x T${v.s_thick_in_str}"`
    },
    update(field, v) {
      let data = {};
      data[field] = v;

      this.updateProductionSheet({
        productionSheetId: this.productionSheet.id,
        data: data
      });
    },
    saveEdits(item) {
      this.updateItem({
        itemId: item.id,
        data: {
          finished_qty: item.finished_qty,
          location: item.location,
          desc: item.desc
        }
      }).then(() => this.editingId = null);
    },
    changedImage(v) {
      let formData = new FormData();
      formData.append('image', v.file, v.file.name);

      this.uploadImage({
        productionSheetId: this.productionSheet.id,
        data: formData
      })
    },
    launchConfirmDelete(itemId) {
      if (!itemId) {
        this.deleteConfirmedCb = () => {
          this.deleteSelected();
        };
        this.confirmDelete.message = `Are you sure you want to delete the ${this.selected.length} selected items?`;
      } else {
        this.deleteConfirmedCb = () => {
          this.deleteItem(itemId).then(
            () => this.confirmDelete.state = false
          );
        };
        this.confirmDelete.message = 'Are you sure you want to delete this item?'
      }

      this.confirmDelete.state = true;
    },
    deleteSelected() {
      this.selected.forEach(id => {
        this.deleteItem(id)
      })
      this.confirmDelete.state = false;
    }
  }
}
</script>

<style lang="scss" scoped>
@import '@/styles/table.scss';
@import '@/styles/printable.scss';

.flex {
  display: flex;
  &--row {
    flex-direction: row;
  }
  &--align-center {
    align-items: center;
  }
  &--justify-center {
    justify-content: center;
  }
}

.is-fullheight {
  height: 100%;
}

@media print {
  .table th {
    font-size: 0.7em !important;
  }
  .table td {
    font-size: 0.7em !important;
  }
  .container {
    padding: 0 !important;
  }
  .column {
    .label {
      font-size: 0.8em !important;
    }
  }
  .productionsheet__info {
    font-size: 0.75em !important;
  }
}

.preview {
  height: 300px;
  img {
    width: 100%;
  }
  iframe {
    width: 100%;
    height: 100%;
    display: block;
  }
  .placeholder {
    background: whitesmoke;
  }
}
</style>